import { createBrowserClient } from "@supabase/ssr";
import type { SupabaseClient } from "@supabase/supabase-js";

// Environment configuration
const isDev = process.env['SUPABASE_ENV'] === 'dev' || process.env['NODE_ENV'] === 'development';

// Initialize Supabase client based on environment
const supabaseUrl = isDev 
  ? process.env['NEXT_PUBLIC_SUPABASE_URL_DEV'] || process.env['NEXT_PUBLIC_SUPABASE_URL']!
  : process.env['NEXT_PUBLIC_SUPABASE_URL_PROD'] || process.env['NEXT_PUBLIC_SUPABASE_URL']!;

const supabaseAnonKey = isDev
  ? process.env['NEXT_PUBLIC_SUPABASE_ANON_KEY_DEV'] || process.env['NEXT_PUBLIC_SUPABASE_ANON_KEY']!
  : process.env['NEXT_PUBLIC_SUPABASE_ANON_KEY_PROD'] || process.env['NEXT_PUBLIC_SUPABASE_ANON_KEY']!;

/**
 * Creates a browser Supabase client for client-side operations
 */
export function getBrowserSupabaseClient<
  DB = any, // eslint-disable-line @typescript-eslint/no-explicit-any -- default preserves untyped back-compat across all consuming apps
>(): SupabaseClient<DB> {
  // Opt into the experimental passkey (WebAuthn) API. Without this flag the
  // auth client's passkey methods (signInWithPasskey / registerPasskey /
  // passkey.*) throw — next-email-client relies on them. Harmless for apps that
  // don't use passkeys: it only enables the methods, it does not change auth.
  //
  // `experimental` is an @experimental option whose presence in
  // @supabase/auth-js's typings varies across patch releases. Defining the auth
  // options as a separate const (rather than an inline object literal at the
  // call site) skips TypeScript's excess-property check, so this compiles across
  // minor supabase version drift while always passing the flag at runtime.
  const authOptions = {
    flowType: 'pkce' as const,
    experimental: { passkey: true },
  };

  // Identify the browser as the origin of every write it makes.
  //
  // `audit.track_changes()` records a channel alongside the actor, and it reads
  // three sources in order: an `app.actor_channel` GUC (server paths and the
  // revert RPCs can SET LOCAL), then this header, then an inference. A browser
  // write can do neither of the first two on its own — PostgREST runs on a
  // pooled connection, so a GUC set in a separate round trip lands on a
  // different session — but PostgREST DOES expose request headers to SQL as
  // `current_setting('request.headers')`. So the header is how a browser write
  // says who it is, and without it every ordinary UI edit was recorded as
  // channel "unknown" even though its actor resolved correctly.
  //
  // It is a HINT, never an authorization input: the trigger validates it against
  // the known channel list and the authoritative actor still comes from
  // `auth.uid()`, which a client cannot forge.
  const globalOptions = {
    headers: { 'x-wtc-channel': 'ui' },
  };

  return createBrowserClient<DB>(supabaseUrl, supabaseAnonKey, {
    auth: authOptions,
    global: globalOptions,
  });
}

// Export the createBrowserClient function for backward compatibility
export { createBrowserClient };
